import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon, ClockIcon, CopyIcon, CreditCardIcon, MapPinIcon, TruckIcon } from 'lucide-react';
import { AccountOrder } from '../../types';
import { ACCOUNT_STATUS_META } from '../../data/account';
import { formatPrice } from '../../utils/format';
import { useCart } from '../../contexts/CartContext';
import { PRODUCTS } from '../../data/products';
interface OrderCardProps {
  order: AccountOrder;
  defaultExpanded?: boolean;
}
export function OrderCard({
  order,
  defaultExpanded = false
}: OrderCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);
  const {
    addItem
  } = useCart();
  const meta = ACCOUNT_STATUS_META[order.status];
  const reorder = () => {
    order.items.forEach(item => {
      const product = PRODUCTS.find(p => p.name === item.name);
      if (product) {
        addItem(product.id, product.colors[0]?.id ?? 'black', item.quantity);
      }
    });
  };
  const copyTracking = () => {
    if (!order.tracking) return;
    navigator.clipboard?.writeText(order.tracking);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  return <article className="border border-white/10 bg-ink-800" data-id="element-40" data-mp="2csm36evrl2me">
      <div className="flex flex-col gap-6 p-5 lg:flex-row lg:items-center" data-id="element-41" data-mp="2cv8k354jouha">
        {/* LEFT */}
        <div className="lg:w-64" data-id="element-42" data-mp="2gwtsoea1qmlo">
          <p className="font-display text-xl leading-none tracking-wide text-pink-500" data-id="element-43" data-mp="1k4qb5nh53yvy">
            {order.id}
          </p>
          <p className="mt-1.5 text-xs text-white/40" data-id="element-44" data-mp="1p4kxddpz00ao">{order.date}</p>
          <div className="mt-4 flex" data-id="element-45" data-mp="1t5emgaf5fl55">
            {order.items.slice(0, 3).map((item, i) => <img key={item.name} src={item.image} alt={item.name} className="h-12 w-12 border border-ink-800 object-cover ring-1 ring-white/10" style={{
            marginLeft: i === 0 ? 0 : -14,
            zIndex: 3 - i
          }} data-id="element-46" data-mp="1y3zq6jtm0myc" data-mpi={i} />)}
          </div>
        </div>

        {/* CENTER */}
        <div className="min-w-0 flex-1" data-id="element-47" data-mp="19j89t2ujk3o7">
          <h3 className="text-base font-semibold text-white" data-id="element-48" data-mp="15g37p1edi403">{order.summary}</h3>
          <p className="mt-1 text-xs text-white/40" data-id="element-49" data-mp="1jfb8fyq6pk6e">
            {order.itemCount}{' '}
            {order.itemCount === 1 ? 'producto' : 'productos'}
          </p>
          <span className={`mt-3 inline-block px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${meta.badge}`} data-id="element-50" data-mp="1eh1e1qcaanlu">
            {meta.label}
          </span>

          {order.note && <p className="mt-3 text-xs text-white/45" data-id="element-51" data-mp="lpq29it1jmnn">{order.note}</p>}

          {order.status === 'enviado' && <div className="mt-5 max-w-md" data-id="element-52" data-mp="vp8l1vx7t2e4">
              <div className="flex items-center" data-id="element-53" data-mp="vo5xnxz3pi2y">
                {order.timeline.map((step, i) => <React.Fragment key={step.label} data-id="element-54" data-mp="7ybk1e6yjphn" data-mpi={i}>
                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${step.done ? 'bg-teal-500 text-black' : 'bg-ink-600 text-white/40'}`} aria-hidden data-id="element-55" data-mp="31l6oymjf7zx">
                      {step.done ? <CheckIcon className="h-3.5 w-3.5" data-id="element-56" data-mp="25pbdjb3el23u" /> : <ClockIcon className="h-3.5 w-3.5" data-id="element-57" data-mp="29q51g4guigwq" />}
                    </span>
                    {i < order.timeline.length - 1 && <span className={`h-[2px] flex-1 ${order.timeline[i + 1].done ? 'bg-teal-500' : 'bg-ink-600'}`} data-id="element-58" data-mp="32eizgk7xpd8a" />}
                  </React.Fragment>)}
              </div>
              <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.1em] text-white/40" data-id="element-59" data-mp="1cyt9o7uuuwpd">
                <span data-id="element-60" data-mp="1hz760sn3gi7k">Recibido</span>
                <span data-id="element-61" data-mp="1hw2xxk6id2bm">Preparando</span>
                <span data-id="element-62" data-mp="1x8cdg3sshovo">Enviado</span>
                <span data-id="element-63" data-mp="1x9dow9dpwddx">Entregado</span>
              </div>
            </div>}
        </div>

        {/* RIGHT */}
        <div className="flex flex-col items-stretch gap-2.5 lg:w-52" data-id="element-64" data-mp="1sezpzwzujo4i">
          <p className="font-display text-3xl leading-none text-white lg:text-right" data-id="element-65" data-mp="1nisteutcboz8">
            {formatPrice(order.total)}
          </p>
          <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} className="flex items-center justify-center gap-2 border border-white px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white hover:text-black" data-id="element-66" data-mp="1jel1ypvhzuxb">
            Ver detalle
            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} data-id="element-67" data-mp="226m88dalqngr" />
          </button>
          {order.status === 'entregado' && <button type="button" onClick={reorder} className="bg-pink-500 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-68" data-mp="32f2gx7qdzfs4">
              Volver a comprar
            </button>}
          {order.status === 'enviado' && <button type="button" className="flex items-center justify-center gap-2 bg-pink-500 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-pink-600" data-id="element-69" data-mp="3g7aa6pigoom2">
              <TruckIcon className="h-3.5 w-3.5" data-id="element-70" data-mp="3v3rjzcb05v6i" />
              Rastrear pedido
            </button>}
        </div>
      </div>

      {/* EXPANDED DETAIL */}
      {expanded && <div className="border-t border-white/10 bg-ink-900/60 p-5" data-id="element-71" data-mp="2xmcbdvzza93">
          <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-72" data-mp="20rb3zjsen2og">
            Productos del pedido
          </h4>
          <ul className="mt-4 divide-y divide-white/5" data-id="element-73" data-mp="3827so69o64d4">
            {order.items.map(item => <li key={item.name} className="flex items-center gap-4 py-3" data-id="element-74" data-mp="3334n8kps1w4m">
                <img src={item.image} alt={item.name} className="h-14 w-14 shrink-0 border border-white/10 object-cover" data-id="element-75" data-mp="33590ht9kg7hk" />
                <div className="min-w-0 flex-1" data-id="element-76" data-mp="3d00z90evx0dx">
                  <p className="truncate text-sm font-semibold text-white" data-id="element-77" data-mp="3r0k1fqwo5c86">
                    {item.name}
                  </p>
                  <p className="text-xs text-white/40" data-id="element-78" data-mp="3m17ihh8x4pav">Color: {item.color}</p>
                </div>
                <span className="text-xs text-white/45" data-id="element-79" data-mp="4k7j4mogtqxe">×{item.quantity}</span>
                <span className="w-24 text-right text-sm text-white" data-id="element-80" data-mp="3vrk0b1pcm4db">
                  {formatPrice(item.price * item.quantity)}
                </span>
              </li>)}
          </ul>

          <div className="mt-6 grid gap-6 border-t border-white/10 pt-6 sm:grid-cols-2" data-id="element-81" data-mp="8nvr36h0eoj6">
            <div data-id="element-82" data-mp="s8e0ocfoqfv4">
              <h4 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-83" data-mp="s9xhq5960uxy">
                <MapPinIcon className="h-3.5 w-3.5 text-pink-500" data-id="element-84" data-mp="x7h11jkpzxwr" />
                Dirección de envío
              </h4>
              <p className="mt-2.5 text-sm leading-relaxed text-white/50" data-id="element-85" data-mp="jak9ffws2cet">
                {order.address}
              </p>
            </div>
            <div data-id="element-86" data-mp="9i4qaf9qvhsc">
              <h4 className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-87" data-mp="9hmmmxkr61ae">
                <CreditCardIcon className="h-3.5 w-3.5 text-pink-500" data-id="element-88" data-mp="eafqlj8ed5lf" />
                Método de pago
              </h4>
              <p className="mt-2.5 text-sm text-white/50" data-id="element-89" data-mp="3vnrxzj31zpma">{order.payment}</p>

              {order.tracking && <div className="mt-5" data-id="element-90" data-mp="3mp6hrul20moo">
                  <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-91" data-mp="3mq98o2idmp4l">
                    N° de seguimiento
                  </h4>
                  <div className="mt-2.5 flex items-center gap-2" data-id="element-92" data-mp="b0eizxi9fd4y">
                    <span className="border border-white/10 bg-ink-800 px-3 py-2 font-mono text-xs text-white" data-id="element-93" data-mp="ayk7tawsize2">
                      {order.tracking}
                    </span>
                    <button type="button" onClick={copyTracking} aria-label="Copiar número de seguimiento" className="border border-white/15 p-2 text-white/60 transition-colors hover:border-pink-500 hover:text-pink-500" data-id="element-94" data-mp="10w07ke35xeh">
                      {copied ? <CheckIcon className="h-3.5 w-3.5 text-teal-400" data-id="element-95" data-mp="3egtwfn1i3zkp" /> : <CopyIcon className="h-3.5 w-3.5" data-id="element-96" data-mp="39j1x47yj1b6h" />}
                    </button>
                  </div>
                </div>}
            </div>
          </div>

          <div className="mt-6 border-t border-white/10 pt-6" data-id="element-97" data-mp="30hc6h4cgyg9r">
            <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/45" data-id="element-98" data-mp="1fzjswyzj0gsn">
              Estado del pedido
            </h4>
            <ol className="mt-5 space-y-0" data-id="element-99" data-mp="1kxlb7hp4oc32">
              {order.timeline.map((step, i) => <li key={step.label} className="flex gap-4" data-id="element-100" data-mp="1p1ax1zef4fke" data-mpi={i}>
                  <div className="flex flex-col items-center" data-id="element-101" data-mp="1tzce6esaa4t8">
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${step.done ? 'bg-teal-500 text-black' : 'border border-white/15 bg-ink-800 text-white/40'}`} aria-hidden data-id="element-102" data-mp="1tyszd6xxjwjf">
                      {step.done ? <CheckIcon className="h-4 w-4" data-id="element-103" data-mp="1b2jlajn135zf" /> : <ClockIcon className="h-3.5 w-3.5" data-id="element-104" data-mp="dkjgutgdd20i" />}
                    </span>
                    {i < order.timeline.length - 1 && <span className={`w-[2px] flex-1 ${order.timeline[i + 1].done ? 'bg-teal-500' : 'bg-white/10'}`} data-id="element-105" data-mp="2mpq6edm9xkx2" />}
                  </div>
                  <div className={i < order.timeline.length - 1 ? 'pb-6' : ''} data-id="element-106" data-mp="30opvtnhm23ua">
                    <p className={`text-sm font-semibold ${step.done ? 'text-white' : 'text-white/40'}`} data-id="element-107" data-mp="30mn7d7w3wiro">
                      {step.label}
                    </p>
                    <p className="mt-0.5 text-xs text-white/35" data-id="element-108" data-mp="2cy408vosmc16">{step.date}</p>
                  </div>
                </li>)}
            </ol>
          </div>
        </div>}
    </article>;
}